import { Widget, Feedback, WidgetType, Project, User, WidgetConfig } from '../types';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database.types';
import {
  isValidProjectName,
  isValidWidgetName,
  isValidEmail,
  isValidUrl,
  isValidComment,
  isValidHexColor,
  isValidBudget,
  sanitizeText,
} from '../utils/validation';

type ProjectRow = Database['public']['Tables']['projects']['Row'];
type ProjectInsert = Database['public']['Tables']['projects']['Insert'];
type ProjectUpdate = Database['public']['Tables']['projects']['Update'];
type WidgetRow = Database['public']['Tables']['widgets']['Row'];
type WidgetInsert = Database['public']['Tables']['widgets']['Insert'];
type WidgetUpdate = Database['public']['Tables']['widgets']['Update'];
type SubmissionRow = Database['public']['Tables']['submissions']['Row'];
type SubmissionInsert = Database['public']['Tables']['submissions']['Insert'];
type Json = Database['public']['Tables']['widgets']['Row']['config'];

// --- Auth ---

export const getSession = async (): Promise<User | null> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) return null;

  return {
    id: session.user.id,
    email: session.user.email || '',
    name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
  };
};

/**
 * Wait for session to be available (useful after sign up/login)
 * Uses onAuthStateChange for reliable session detection
 */
export const waitForSession = async (maxWaitMs: number = 5000): Promise<User | null> => {
  // First check if session is already available
  const existingUser = await getSession();
  if (existingUser) return existingUser;

  // If not, wait for auth state change
  return new Promise(resolve => {
    const timeout = setTimeout(() => {
      subscription.unsubscribe();
      resolve(null);
    }, maxWaitMs);

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        clearTimeout(timeout);
        subscription.unsubscribe();
        resolve({
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata?.name || session.user.email?.split('@')[0] || 'User',
        });
      }
    });
  });
};

export const signUp = async (name: string, email: string, password: string): Promise<User> => {
  if (!password || password.length < 8) {
    throw new Error('Password is required and must be at least 8 characters');
  }

  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: { name },
    },
  });

  if (error) throw error;
  if (!data.user) throw new Error('Sign up failed');

  // If session is returned directly from signUp, the user is immediately signed in
  // (no email confirmation required). Store this for immediate use.
  if (data.session) {
    // Session is available immediately - user is logged in
    return {
      id: data.session.user.id,
      email: data.session.user.email || '',
      name: data.session.user.user_metadata?.name || name,
    };
  }

  // If no session (email confirmation required), return user info
  // The session will be established after email confirmation
  const user: User = {
    id: data.user.id,
    email: data.user.email || '',
    name: data.user.user_metadata?.name || name,
  };

  return user;
};

export const signIn = async (email: string, password: string): Promise<User> => {
  if (!password) {
    throw new Error('Password is required');
  }

  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) {
    throw error;
  }

  if (!data.user) throw new Error('Sign in failed');

  return {
    id: data.user.id,
    email: data.user.email || '',
    name: data.user.user_metadata?.name || email.split('@')[0],
  };
};

export const signOut = async () => {
  await supabase.auth.signOut();
};

// --- Projects ---

// Helper function to convert database ProjectRow to application Project type
const mapProjectRowToProject = (row: ProjectRow): Project => {
  return {
    id: row.id,
    owner_id: row.owner_id,
    name: row.name,
    url: row.url,
    created_at: row.created_at,
    // Styling fields from database
    brand_color: row.brand_color || '#f59e0b',
    brand_icon: row.brand_icon || 'briefcase',
    widget_padding: row.widget_padding ?? 16,
    widget_border_radius: row.widget_border_radius ?? 12,
    custom_css: row.custom_css || null,
    // Legacy aliases for backward compatibility
    color: row.brand_color || '#f59e0b',
    icon: row.brand_icon || 'briefcase',
    application_type: row.application_type || undefined,
    primary_use_case: row.primary_use_case || undefined,
  };
};

// One-time cleanup: Remove old localStorage keys for project metadata
// This runs once to migrate away from localStorage
const cleanupLegacyLocalStorage = () => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('verivibe_project_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch {
    // Silently handle localStorage cleanup failures
  }
};

// Run cleanup on module load
cleanupLegacyLocalStorage();

export const getProjects = async (): Promise<Project[]> => {
  const { data, error } = await supabase
    .from('projects')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!data) return [];

  // Styling is now stored in database - no localStorage needed
  return (data as ProjectRow[]).map(row => mapProjectRowToProject(row));
};

export const createProject = async (
  name: string,
  url: string,
  color?: string,
  icon?: string,
  _application_type?: string,
  _primary_use_case?: string
): Promise<Project> => {
  const user = await getSession();

  if (!user) {
    throw new Error('Not authenticated - no valid user session found');
  }

  if (!name || typeof name !== 'string' || name.trim() === '') {
    throw new Error('Project name is required and must be a non-empty string');
  }

  if (!isValidProjectName(name)) {
    throw new Error(
      'Project name must be 1-100 characters and contain only letters, numbers, spaces, hyphens, underscores, or periods'
    );
  }

  if (url && !isValidUrl(url)) {
    throw new Error('Project URL must be a valid http or https URL');
  }

  const insertData: ProjectInsert = {
    owner_id: user.id,
    name: sanitizeText(name.trim(), 100),
    url: url || '',
    brand_color: color || '#6366f1',
    brand_icon: icon || 'briefcase',
    widget_padding: 16,
    widget_border_radius: 12,
  };

  const { data, error } = await supabase
    .from('projects')
    // @ts-expect-error - Supabase type inference issue with Database generic
    .insert([insertData])
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create project: ${error.message}`);
  }

  if (!data) {
    throw new Error('Failed to create project: No data returned from database');
  }

  return mapProjectRowToProject(data as ProjectRow);
};

export const getProjectById = async (id: string): Promise<Project | null> => {
  const { data, error } = await supabase.from('projects').select('*').eq('id', id).single();
  if (error || !data) return null;
  return mapProjectRowToProject(data as ProjectRow);
};

export const updateProject = async (
  id: string,
  updates: Partial<Project>
): Promise<Project | undefined> => {
  // Validate inputs before updating
  if (updates.name !== undefined && !isValidProjectName(updates.name)) {
    throw new Error(
      'Project name must be 1-100 characters and contain only letters, numbers, spaces, hyphens, underscores, or periods'
    );
  }
  if (updates.url !== undefined && updates.url !== '' && !isValidUrl(updates.url)) {
    throw new Error('Project URL must be a valid http or https URL');
  }
  const resolvedColor = updates.brand_color ?? updates.color;
  if (resolvedColor !== undefined && !isValidHexColor(resolvedColor)) {
    throw new Error('Brand color must be a valid hex color (e.g. #ff0000)');
  }

  // Extract fields that exist in the database schema (including styling)
  const dbUpdates: ProjectUpdate = {};
  if (updates.name !== undefined) dbUpdates.name = sanitizeText(updates.name.trim(), 100);
  if (updates.url !== undefined) dbUpdates.url = updates.url;
  if (updates.application_type !== undefined) dbUpdates.application_type = updates.application_type;
  if (updates.primary_use_case !== undefined) dbUpdates.primary_use_case = updates.primary_use_case;
  // Styling fields are now stored in database
  if (updates.brand_color !== undefined) dbUpdates.brand_color = updates.brand_color;
  if (updates.brand_icon !== undefined) dbUpdates.brand_icon = updates.brand_icon;
  if (updates.widget_padding !== undefined) dbUpdates.widget_padding = updates.widget_padding;
  if (updates.widget_border_radius !== undefined)
    dbUpdates.widget_border_radius = updates.widget_border_radius;
  if (updates.custom_css !== undefined) dbUpdates.custom_css = updates.custom_css;
  // Support legacy color/icon aliases
  if (updates.color !== undefined && updates.brand_color === undefined)
    dbUpdates.brand_color = updates.color;
  if (updates.icon !== undefined && updates.brand_icon === undefined)
    dbUpdates.brand_icon = updates.icon;

  const { data, error } = await supabase
    .from('projects')
    // @ts-expect-error - Supabase type inference issue with Database generic
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  if (!data) return undefined;

  return mapProjectRowToProject(data as ProjectRow);
};

export const deleteProject = async (id: string): Promise<void> => {
  const { error } = await supabase.from('projects').delete().eq('id', id);

  if (error) throw error;
};

// --- Widgets ---

// Helper function to convert database WidgetRow to application Widget type
const mapWidgetRowToWidget = (row: WidgetRow): Widget => {
  return {
    id: row.id,
    project_id: row.project_id,
    user_id: row.user_id,
    name: row.name,
    type: row.type,
    config: row.config as unknown as WidgetConfig,
    created_at: row.created_at,
    view_count: (row as Record<string, unknown>).view_count as number | undefined,
  };
};

export const getWidgets = async (projectId?: string): Promise<Widget[]> => {
  let query = supabase.from('widgets').select('*');

  if (projectId) {
    query = query.eq('project_id', projectId);
  }

  const { data, error } = await query.order('created_at', { ascending: false });

  if (error) throw error;
  if (!data) return [];

  return (data as WidgetRow[]).map(mapWidgetRowToWidget);
};

export const createWidget = async (
  projectId: string,
  name: string,
  type: WidgetType,
  config: WidgetConfig
): Promise<Widget> => {
  const user = await getSession();

  if (!user) {
    throw new Error('Not authenticated - no valid user session found');
  }

  if (!projectId || typeof projectId !== 'string') {
    throw new Error('Project ID is required and must be a valid string');
  }

  if (!name || typeof name !== 'string' || name.trim() === '') {
    throw new Error('Widget name is required and must be a non-empty string');
  }

  if (!isValidWidgetName(name)) {
    throw new Error(
      'Widget name must be 1-100 characters and contain only letters, numbers, spaces, hyphens, underscores, periods, or common punctuation'
    );
  }

  if (!type) {
    throw new Error('Widget type is required');
  }

  if (!config || typeof config !== 'object') {
    throw new Error('Widget config is required and must be an object');
  }

  // Validate profilePictureUrl if provided
  if (config.profilePictureUrl && !isValidUrl(config.profilePictureUrl)) {
    throw new Error('Profile picture URL must be a valid http or https URL');
  }

  const insertData: WidgetInsert = {
    project_id: projectId,
    user_id: user.id,
    name: sanitizeText(name.trim(), 100),
    type,
    config: config as unknown as Json,
  };

  const { data, error } = await supabase
    .from('widgets')
    // @ts-expect-error - Supabase type inference issue with Database generic
    .insert([insertData])
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to create widget: ${error.message}`);
  }

  if (!data) {
    throw new Error('Failed to create widget: No data returned from database');
  }

  return mapWidgetRowToWidget(data as WidgetRow);
};

export const updateWidget = async (
  id: string,
  updates: Partial<Widget>
): Promise<Widget | undefined> => {
  // Validate inputs before updating
  if (updates.name !== undefined && !isValidWidgetName(updates.name)) {
    throw new Error(
      'Widget name must be 1-100 characters and contain only letters, numbers, spaces, hyphens, underscores, periods, or common punctuation'
    );
  }

  // Validate profilePictureUrl in config if present
  if (updates.config?.profilePictureUrl && !isValidUrl(updates.config.profilePictureUrl)) {
    throw new Error('Profile picture URL must be a valid http or https URL');
  }

  // Extract only fields that exist in the database schema
  const dbUpdates: WidgetUpdate = {};
  if (updates.name !== undefined) dbUpdates.name = sanitizeText(updates.name.trim(), 100);
  if (updates.type !== undefined) dbUpdates.type = updates.type;
  if (updates.config !== undefined) dbUpdates.config = updates.config as unknown as Json;

  const { data, error } = await supabase
    .from('widgets')
    // @ts-expect-error - Supabase type inference issue with Database generic
    .update(dbUpdates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  if (!data) return undefined;

  return mapWidgetRowToWidget(data as WidgetRow);
};

export const getWidgetById = async (id: string): Promise<Widget | undefined> => {
  const { data, error } = await supabase.from('widgets').select('*').eq('id', id).single();

  if (error || !data) return undefined;
  return mapWidgetRowToWidget(data as WidgetRow);
};

export const deleteWidget = async (id: string): Promise<void> => {
  const { error } = await supabase.from('widgets').delete().eq('id', id);

  if (error) throw error;
};

// --- Feedback ---

// Helper function to convert database SubmissionRow to application Feedback type
const mapSubmissionRowToFeedback = (row: SubmissionRow): Feedback => {
  return {
    id: row.id,
    widget_id: row.widget_id,
    project_id: row.project_id,
    response_type: row.response_type,
    response_value: row.response_value,
    comment: row.comment || undefined,
    budget: row.budget || undefined,
    is_audio_transcript: row.is_audio_transcript,
    ai_metadata: row.ai_metadata as Record<string, unknown>,
    sentiment_score: row.sentiment_score || undefined,
    sentiment: row.sentiment || undefined,
    category: row.category || undefined,
    user_id: row.user_id || undefined,
    user_avatar: row.user_avatar || undefined,
    user_traits: (row.user_traits as Record<string, unknown>) || {},
    created_at: row.created_at,
  };
};

export const getFeedbackForWidget = async (widgetId: string): Promise<Feedback[]> => {
  const { data, error } = await supabase
    .from('submissions')
    .select('*')
    .eq('widget_id', widgetId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false });

  if (error) throw error;
  if (!data) return [];

  return (data as SubmissionRow[]).map(mapSubmissionRowToFeedback);
};

export const getFeedbackCounts = async (widgetIds: string[]): Promise<Record<string, number>> => {
  const { data, error } = await supabase
    .from('submissions')
    .select('widget_id')
    .in('widget_id', widgetIds)
    .is('deleted_at', null);

  if (error) throw error;

  const counts: Record<string, number> = {};
  widgetIds.forEach(id => (counts[id] = 0));

  data?.forEach(f => {
    // @ts-expect-error - Supabase type inference issue with Database generic
    const widgetId = f.widget_id;
    if (widgetId !== undefined && counts[widgetId] !== undefined) {
      counts[widgetId]++;
    }
  });

  return counts;
};

export const submitFeedback = async (
  widgetId: string,
  payload: Partial<Feedback>
): Promise<Feedback> => {
  // Get widget to retrieve project_id
  const widget = await getWidgetById(widgetId);
  if (!widget) throw new Error('Widget not found');

  // Validate email if provided
  if (payload.email && !isValidEmail(payload.email)) {
    throw new Error('Invalid email address');
  }

  // Validate comment length if provided
  if (payload.comment && !isValidComment(payload.comment)) {
    throw new Error('Comment must be 2000 characters or fewer');
  }

  // Validate budget if provided
  if (payload.budget && !isValidBudget(payload.budget)) {
    throw new Error('Invalid budget value');
  }

  const insertData: SubmissionInsert = {
    widget_id: widgetId,
    project_id: widget.project_id,
    response_type: payload.response_type || 'email_capture',
    response_value: String(payload.response_value || ''),
    comment: payload.comment ? sanitizeText(payload.comment, 2000) : null,
    budget: payload.budget || null,
    is_audio_transcript: payload.is_audio_transcript || false,
    user_id: payload.user_id || null,
    user_avatar: payload.user_avatar || null,
    sentiment: payload.sentiment || null,
    sentiment_score: payload.sentiment_score || null,
    user_traits: (payload.user_traits as unknown as Json) || ({} as Json),
    ai_metadata: (payload.ai_metadata as unknown as Json) || ({} as Json),
    email: payload.email || null,
    path_taken: payload.path_taken || null,
  };

  const { data, error } = await supabase
    .from('submissions')
    // @ts-expect-error - Supabase type inference issue with Database generic
    .insert([insertData])
    .select()
    .single();

  if (error) throw error;
  if (!data) throw new Error('Failed to submit feedback');

  return mapSubmissionRowToFeedback(data as SubmissionRow);
};

/**
 * Fetch widget emails from the widget_emails view
 * Returns emails grouped by path (positive/negative)
 */
export const getWidgetEmails = async (
  widgetId: string
): Promise<{ positive: string[]; negative: string[] }> => {
  const { data, error } = await supabase
    .from('widget_emails')
    .select('email, path_taken')
    .eq('widget_id', widgetId)
    .order('created_at', { ascending: false });

  if (error) {
    return { positive: [], negative: [] };
  }

  const positive: string[] = [];
  const negative: string[] = [];

  data?.forEach(row => {
    // @ts-expect-error - Supabase type inference issue with views
    if (row.email) {
      // @ts-expect-error - Supabase type inference issue with views
      if (row.path_taken === 'positive') {
        // @ts-expect-error - Supabase type inference issue with views
        positive.push(row.email);
        // @ts-expect-error - Supabase type inference issue with views
      } else if (row.path_taken === 'negative') {
        // @ts-expect-error - Supabase type inference issue with views
        negative.push(row.email);
      }
    }
  });

  return { positive, negative };
};

// --- Submission Deletion (GDPR Art. 17 compliance) ---

export const deleteSubmission = async (id: string): Promise<void> => {
  const { error } = await supabase.from('submissions').delete().eq('id', id);
  if (error) throw error;
};

// --- Account Deletion (GDPR Art. 17 compliance) ---

/**
 * Delete the current user's account and all associated data.
 * Cascade deletion removes: projects -> widgets -> submissions.
 * Auth record is deleted via Supabase admin API (Edge Function required),
 * so we delete all user data first, then sign out.
 */
export const deleteAccount = async (): Promise<void> => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('Not authenticated');

  const userId = session.user.id;

  // Delete all projects owned by the user (cascades to widgets + submissions)
  const { error: projectsError } = await supabase.from('projects').delete().eq('owner_id', userId);

  if (projectsError) throw new Error('Failed to delete projects: ' + projectsError.message);

  // Sign out (clears auth session)
  await supabase.auth.signOut();
};

// Seed functionality is handled by the initial SQL and Supabase Auth triggers
export const seedData = () => {
  // No-op: seed data is managed via Supabase dashboard/SQL
};

// --- Storage ---

/**
 * Upload a profile image to Supabase Storage
 * Returns the public URL of the uploaded image
 */
export const uploadProfileImage = async (file: File): Promise<string> => {
  const user = await getSession();
  if (!user) throw new Error('Must be logged in to upload images');

  // Validate file type
  const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPG, PNG, GIF, or WebP image.');
  }

  // Validate file size (max 2MB)
  const maxSize = 2 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 2MB.');
  }

  // Generate unique filename
  const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const fileName = `${user.id}/${Date.now()}.${fileExt}`;

  // Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from('profile-images')
    .upload(fileName, file, {
      cacheControl: '3600',
      upsert: false,
    });

  if (uploadError) {
    throw new Error(`Upload failed: ${uploadError.message}`);
  }

  // Get public URL
  const { data: urlData } = supabase.storage.from('profile-images').getPublicUrl(fileName);

  return urlData.publicUrl;
};
